import { Eye, EyeOff } from 'lucide-react';

import clsx from 'clsx';
import { useRef, useState } from 'react';
import { startTransition } from 'react';
import {
  Control,
  FieldError,
  FieldErrorsImpl,
  FieldValues,
  Merge,
  RegisterOptions,
  UseFormRegister,
  useController,
  useFormContext,
} from 'react-hook-form';

import { useScreenSize } from '@/hooks/useScreenSize';

import { errorMsgContainerClassMap } from '../ui/Purchase/payment-details/style';
import { inputFormSpaceContainerClassMap } from '../ui/variants/about-you-user-detail-form/style';

interface InputFieldProps {
  type: string;
  placeholder: string;
  register: UseFormRegister<FieldValues>;
  errors?: FieldError | Merge<FieldError, FieldErrorsImpl<any>> | undefined;
  name: string;
  showPasswordToggle?: boolean;
  icon?: JSX.Element;
  pattern?: RegExp;
  validate?: (value: any) => true | string;
  className?: string;
  iconClassName?: string;
  control: Control<any>;
  onClick?: () => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void; // ✅ Add this line
  containerClassName?: string;
  disabled?: boolean;
  onChange?: (e: any, inputRef?: any) => void;
  maxLength?: number;
  minLength?: number;
  errorMsg?: string;
  rules?: RegisterOptions;
  readOnly?: boolean;
  inputRef?: React.MutableRefObject<HTMLInputElement | null>;
}

const InputField = ({
  type,
  placeholder,
  register,
  name,
  showPasswordToggle = false,
  icon,
  pattern,
  validate,
  className = '',
  iconClassName = '',
  control,
  onClick,
  onBlur, // ✅ Receive the onBlur prop
  containerClassName,
  disabled = false,
  onChange,
  maxLength,
  minLength,
  errorMsg,
  rules,
  readOnly = false,
  inputRef: externalInputRef,
}: InputFieldProps) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const { getSize } = useScreenSize();
  const size = getSize;
  const isPaymentFields = ['accountNumber'].includes(name);
  const handleTogglePassword = () => setShowPassword(!showPassword);
  const { setValue } = useFormContext();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: '',
    rules: {
      required: `${errorMsg} is required.`,
      pattern: pattern && {
        value: pattern,
        message: 'Invalid format.',
      },
      validate: validate,
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value, selectionStart } = e.target;

    startTransition(() => {
      setValue(name, value);
    });

    // Restore cursor position after re-render
    requestAnimationFrame(() => {
      if (inputRef.current && selectionStart !== null) {
        inputRef.current.setSelectionRange(selectionStart, selectionStart);
      }
    });

    if (onChange) onChange(e, inputRef); // Call external handler if provided
  };

  return (
    <div className={clsx('relative', containerClassName, { isPaymentFields: 'mt-8' })}>
      <input
        {...field}
        {...register(name, rules)}
        ref={element => {
          inputRef.current = element;
          field.ref(element);

          if (externalInputRef) {
            externalInputRef.current = element;
          }
        }}
        type={showPasswordToggle && showPassword ? 'text' : type}
        placeholder={placeholder}
        onChange={handleChange}
        onClick={() => onClick?.()}
        className={clsx(
          `${className} border border-color-background-input-default text-color-content-text-primary placeholder:text-color-content-text-secondary focus:border-color-border-Input-focused focus:outline-none ${disabled ? 'cursor-not-allowed opacity-50' : ''}`,
          inputFormSpaceContainerClassMap[size]
        )}
        maxLength={maxLength}
        minLength={minLength}
        onBlur={e => {
          field.onBlur(); // ✅ Let react-hook-form handle blur
          onBlur?.(e); // ✅ Call external onBlur if passed
        }}
        disabled={disabled}
        readOnly={readOnly}
        autoComplete="off"
      />{' '}
      {icon ? (
        <div
          className={clsx(
            'pointer-events-none absolute right-4 top-2 flex items-center justify-center',
            iconClassName
          )}
        >
          {icon}
        </div>
      ) : null}
      {showPasswordToggle ? (
        <button type="button" onClick={handleTogglePassword} className="absolute right-3 top-3">
          {showPassword ? (
            <EyeOff className="h-5 w-5 text-gray-400" />
          ) : (
            <Eye className="h-5 w-5 text-gray-400" />
          )}
        </button>
      ) : null}
      {error && !rules && !isPaymentFields ? (
        <p className={errorMsgContainerClassMap[size]}>{error.message}</p>
      ) : null}
    </div>
  );
};

export default InputField;
